import {
  applyRegistrarState,
  readShifts,
  requestRegistrarState,
} from "@prostocheck/fiscal";
import {
  createSuccessResult,
  DEFAULT_HISTORY_DAYS,
  IFiscalClient,
  IRegisterWithOwner,
  SimpleResult,
  toRecentRange,
} from "@prostocheck/shared";

/**
 * The register list itself comes from the session — `Objects` is answered once
 * during sign-in. What it cannot tell us is whether a shift is open or when the
 * last Z-report arrived; both cost one call per register.
 *
 * A register whose detail calls fail keeps what `Objects` knew about it: a
 * failing detail must not hide a real cash register.
 */
export const fetchRegisterDetails = async ({
  client,
  registers,
}: {
  client: IFiscalClient;
  registers: IRegisterWithOwner[];
}): Promise<SimpleResult<IRegisterWithOwner[]>> => {
  const detailed = await Promise.all(
    registers.map(register => enrichRegister({ client, register })),
  );

  return createSuccessResult(detailed);
};

const enrichRegister = async ({
  client,
  register,
}: {
  client: IFiscalClient;
  register: IRegisterWithOwner;
}): Promise<IRegisterWithOwner> => {
  const [state, lastZReportAt] = await Promise.all([
    requestRegistrarState({
      client,
      registrarFiscalNumber: register.fiscalNumber,
    }),
    findLastZReportAt({ client, registrarFiscalNumber: register.fiscalNumber }),
  ]);

  return {
    ...applyRegistrarState({
      register,
      state: state.success ? (state.data ?? null) : null,
    }),
    lastZReportAt,
  };
};

/** Most recent closed shift within the default window, or nothing. */
const findLastZReportAt = async ({
  client,
  registrarFiscalNumber,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
}): Promise<string | undefined> => {
  const result = await readShifts({
    client,
    registrarFiscalNumber,
    ...toRecentRange(DEFAULT_HISTORY_DAYS),
  });

  const closed = (result.data ?? [])
    .filter(shift => Boolean(shift.Closed) && Boolean(shift.ZRepFiscalNum))
    .map(shift => shift.Closed!)
    .sort();

  return closed.at(-1);
};

export const findCashRegister = ({
  registers,
  fiscalNumber,
}: {
  registers: IRegisterWithOwner[];
  fiscalNumber: string | undefined;
}): IRegisterWithOwner | undefined =>
  registers.find(register => register.fiscalNumber === fiscalNumber);
